import React, { useEffect, useRef, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import styled, { createGlobalStyle, css } from 'styled-components';

import { isValidEmail } from './accountAuth';
import { clearPendingFromUrl, useOtpSignIn } from './useOtpSignIn';
import { gaOtpFailed, gaOtpRequested, gaOtpVerified } from '../lib/gaPixel';

/**
 * OTP-only sign-in. One mechanism — a 6-digit email code — that works
 * identically in the Instagram/Facebook webview and in real browsers, so the
 * OAuth-in-webview failure modes (Google's `disallowed_useragent`, Apple's
 * wiped-storage redirect) simply stop existing. Google, Apple, and email +
 * password are all gone.
 *
 * Signup and login are ONE flow: enter email → get code → in. The server
 * creates the account if new, finds it if existing (any original provider),
 * and upgrades an anon-with-a-board in place (same uid). The user never sees a
 * branch — there is no "create account / sign in" toggle to answer.
 *
 * `defaultMode` DOES drive the heading, though. An earlier version of this file
 * said "OTP is one flow, so this no longer branches" and hardcoded "Save your
 * gifts" for all eleven entry points — so clicking **Sign in** opened a modal
 * titled *Save your gifts* (Kate, 2026-07-24). That conflated one MECHANISM
 * with one FRAMING: because OTP removed the provider buttons, the words are now
 * the only thing carrying context, so they have to carry it. One flow, one
 * component, one field — the sign on the door changes with the door.
 *
 * Note the distinction from the spec's "no toggle" rule: that forbids a control
 * the USER has to answer. Per-entry-point copy is the opposite — the product
 * answering for them.
 */

export type AuthMode = 'signin' | 'signup';

/**
 * Heading per entry intent. A `Record` (not a lookup with a fallback) so adding
 * an `AuthMode` without giving it words is a compile error rather than a silent
 * inheritance of someone else's framing — which is exactly how "Save your
 * gifts" ended up on the sign-in door.
 *
 * The subhead is deliberately constant: the mechanism is identical either way,
 * and saying so plainly ("no password") is reassurance, not context.
 */
const INTENT_HEADING: Record<AuthMode, string> = {
  signin: 'Sign in',
  signup: 'Save your gifts',
};

export interface SignInModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Entry intent. Drives the heading only — the flow itself never branches. */
  defaultMode?: AuthMode;
  /**
   * Fires once after a successful sign-in. Used by AuthGate to commit a
   * "save-intent" action (e.g. the heart click that opened the modal).
   * Awaited before the modal closes.
   */
  onAuthed?: () => void | Promise<void>;
}

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_MS = 30_000;

/**
 * react-bootstrap's `<Modal>` provides the dialog primitive but its default
 * chrome clashes with the layout we want; we override `Modal.Dialog` styles via
 * a portal-targeted global rule because the dialog renders outside the React
 * tree at runtime. On small viewports it drops to a bottom sheet.
 */
const ModalGlobalStyles = createGlobalStyle`
  .thea-auth-modal .modal-dialog {
    max-width: 400px;
    margin: 1.75rem auto;
  }
  .thea-auth-modal .modal-content {
    border: 0;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  }
  @media (max-width: 640px) {
    .thea-auth-modal .modal-dialog {
      margin: 0;
      align-items: flex-end;
      min-height: 100vh;
      display: flex;
      max-width: none;
    }
    .thea-auth-modal .modal-content {
      width: 100%;
      border-radius: 12px 12px 0 0;
      max-height: 85vh;
    }
  }
`;

const ModalChrome = styled.div`
  position: relative;
  padding: 24px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: ${({ theme }) => theme.color.mutedText};
  cursor: pointer;
  transition: background-color 150ms ease;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const Wordmark = styled.div`
  text-align: center;
  margin-bottom: 6px;
  padding-right: 24px;
  span {
    font-family: ${({ theme }) => theme.font.serif};
    font-style: italic;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.color.clay};
    font-size: 26px;
    line-height: 1;
  }
`;

const Heading = styled.h2`
  font-family: ${({ theme }) => theme.font.serif};
  text-align: center;
  font-size: 22px;
  color: #1a1a1a;
  line-height: 1.2;
  padding-right: 24px;
  margin: 0;
`;

const Subhead = styled.p`
  font-family: ${({ theme }) => theme.font.sans};
  text-align: center;
  font-size: 14px;
  line-height: 1.45;
  color: ${({ theme }) => theme.color.mutedText};
  margin: 8px 0 0;
  padding-right: 24px;
  strong {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const Spacer = styled.div<{ $h: number }>`
  height: ${({ $h }) => $h}px;
`;

const baseControl = css`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  font-size: 15px;
  font-family: ${({ theme }) => theme.font.sans};
  transition: opacity 150ms ease;
`;

const Input = styled.input<{ $error?: boolean }>`
  ${baseControl}
  font-size: 16px; /* <16px makes iOS Safari zoom the page on focus, and the zoom outlives this modal. */
  padding: 0 14px;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.color.errorRed : theme.color.warmBorder)};
  background: #ffffff;
  color: #1a1a1a;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 2px;
  }
`;

const CodeRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const CodeInput = styled.input<{ $error?: boolean }>`
  flex: 1;
  min-width: 0;
  height: 56px;
  text-align: center;
  font-size: 22px;
  font-family: ${({ theme }) => theme.font.sans};
  font-weight: 600;
  color: #1a1a1a;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.color.errorRed : theme.color.warmBorder)};
  background: #ffffff;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 2px;
  }
`;

const ErrorText = styled.p`
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 13px;
  color: ${({ theme }) => theme.color.errorRed};
  margin: 10px 0 0;
  text-align: center;
`;

const PrimaryButton = styled.button`
  ${baseControl}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${({ theme }) => theme.color.clay};
  color: #ffffff;
  border: 0;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const LinkRow = styled.div`
  text-align: center;
  margin-top: 14px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 13px;
  color: ${({ theme }) => theme.color.mutedText};
  button {
    background: transparent;
    border: 0;
    color: ${({ theme }) => theme.color.clay};
    font-weight: 500;
    cursor: pointer;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
`;

export const SignInModal: React.FC<SignInModalProps> = ({
  open,
  onOpenChange,
  defaultMode = 'signin',
  onAuthed,
}) => {
  const fireAuthed = async () => {
    try {
      await onAuthed?.();
    } catch (err) {
      console.error('[SignInModal] onAuthed callback failed:', err);
    }
  };

  // Dismissing the modal (X, Esc, backdrop) must clear the URL-carried pending
  // OTP state — otherwise otp_email/otp_step linger in the address bar after
  // the user backs out. Previously only "Use a different email" cleared them.
  const handleClose = () => {
    clearPendingFromUrl();
    onOpenChange(false);
  };

  return (
    <>
      <ModalGlobalStyles />
      <Modal
        show={open}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard
        dialogClassName="thea-auth-modal"
        aria-labelledby="thea-auth-heading"
      >
        <ModalChrome>
          <CloseButton type="button" onClick={handleClose} aria-label="Close">
            <CloseGlyph />
          </CloseButton>
          <OtpBody
            open={open}
            intent={defaultMode}
            onSuccess={async () => {
              await fireAuthed();
              onOpenChange(false);
            }}
          />
        </ModalChrome>
      </Modal>
    </>
  );
};

type Step = 'email' | 'code' | 'done';

/**
 * How long the "You're signed in" confirmation holds before the modal closes.
 * Long enough to read, short enough that nobody waits on it. Mirrors the
 * capture sheet's confirmation, which has always had one — the *less*
 * consequential of the two events.
 */
const SUCCESS_HOLD_MS = 1_600;

interface OtpBodyProps {
  open: boolean;
  intent: AuthMode;
  onSuccess: () => Promise<void> | void;
}

const OtpBody: React.FC<OtpBodyProps> = ({ open, intent, onSuccess }) => {
  const { initialPending, requestCode, verifyCode, clearPending } = useOtpSignIn();

  const [step, setStep] = useState<Step>(initialPending ? 'code' : 'email');
  const [email, setEmail] = useState(initialPending?.email ?? '');
  const [code, setCode] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const codeInputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Hold the success confirmation briefly, then hand off to the host (which
  // fires onAuthed and closes). Bounded and self-cancelling: if the modal
  // unmounts first the timer is cleared, so a slow onAuthed can never strand
  // the user on a frozen "You're signed in" card.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  useEffect(() => {
    if (step !== 'done') return;
    const id = window.setTimeout(() => {
      void onSuccessRef.current();
    }, SUCCESS_HOLD_MS);
    return () => window.clearTimeout(id);
  }, [step]);

  // Reset the whole flow whenever the modal is (re)opened WITHOUT a URL-carried
  // pending code step. A URL-restored code step (webview app-switch survival)
  // is honored instead of reset.
  useEffect(() => {
    if (!open) return;
    if (initialPending) {
      setStep('code');
      setEmail(initialPending.email);
    } else {
      setStep('email');
      setEmail('');
    }
    setCode(Array(CODE_LENGTH).fill(''));
    setError('');
    setBusy(false);
    setCooldownUntil(0);
    // initialPending is latched at mount, so this runs on open transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const focusCodeInput = (i: number) => {
    codeInputsRef.current[i]?.focus();
    codeInputsRef.current[i]?.select();
  };

  const startCooldown = () => setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);

  /**
   * Advance to the code step IMMEDIATELY, then fire the request in the
   * background. Kate hit a ~20s spinner here (2026-07-24); the backend cause is
   * fixed separately, but awaiting was wrong on its own terms:
   *
   *   `theaWebRequestEmailOtp` returns a hardcoded `{"success": true}` BY
   *   SECURITY DESIGN — it must not become an account-enumeration oracle. The
   *   response therefore carries zero user-visible information, and the address
   *   is already validated client-side. The await bought a spinner and nothing
   *   else.
   *
   * The sibling capture sheet has always done exactly this ("flips to Saved
   * IMMEDIATELY... so the user never waits on the callable's cold-start +
   * Klaviyo round-trip"). Same problem, same repo — this brings sign-in in line.
   *
   * A genuine send failure still surfaces, on the code step, where "Resend" and
   * "Use a different email" are both already sitting.
   */
  const sendCode = async (targetEmail: string) => {
    setError('');
    setCode(Array(CODE_LENGTH).fill(''));
    setStep('code');
    startCooldown();
    gaOtpRequested({ surface: 'auth_modal' });
    // Focus the first code box after the step renders.
    setTimeout(() => focusCodeInput(0), 0);

    try {
      await requestCode(targetEmail);
    } catch (err) {
      console.error('[SignInModal] requestEmailOtp failed:', err);
      setError("We couldn't send a code — check the address and try again.");
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setEmail(trimmed);
    await sendCode(trimmed);
  };

  const submitCode = async (codeStr: string) => {
    if (codeStr.length !== CODE_LENGTH || busy) return;
    setBusy(true);
    setError('');
    try {
      await verifyCode(email.trim(), codeStr);
      gaOtpVerified({ surface: 'auth_modal' });
      // Say it worked before vanishing. Previously the modal just unmounted:
      // "The code modal disappeared, making me think it had worked, but then
      // the homepage stayed in the signed out mode" (Kate, 2026-07-24). The
      // spec asked for this from the start — webview-email-otp-signin-spec.md
      // "email step → code step → inline `Signed in ✨`".
      setStep('done');
      return;
    } catch (err) {
      console.error('[SignInModal] verifyEmailOtp failed:', err);
      gaOtpFailed({ surface: 'auth_modal' });
      setError("That code didn't work — check it or resend.");
      setCode(Array(CODE_LENGTH).fill(''));
      setTimeout(() => focusCodeInput(0), 0);
    } finally {
      setBusy(false);
    }
  };

  const setDigit = (index: number, raw: string) => {
    // Keep only the last typed digit; ignore non-digits.
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = [...code];
    next[index] = digit;
    setCode(next);
    if (error) setError('');
    if (digit && index < CODE_LENGTH - 1) {
      setTimeout(() => focusCodeInput(index + 1), 0);
    }
    const joined = next.join('');
    // Auto-submit the moment all six digits are present (with the Verify
    // button as the explicit/a11y fallback).
    if (joined.length === CODE_LENGTH) void submitCode(joined);
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      focusCodeInput(index - 1);
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const digits = (e.clipboardData?.getData('text') ?? '')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);
    if (!digits) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < digits.length; i += 1) next[i] = digits[i];
    setCode(next);
    setError('');
    const last = Math.min(digits.length, CODE_LENGTH) - 1;
    setTimeout(() => focusCodeInput(last), 0);
    if (digits.length === CODE_LENGTH) void submitCode(digits);
  };

  const handleResend = async () => {
    if (Date.now() < cooldownUntil || busy) return;
    await sendCode(email.trim());
  };

  const handleChangeEmail = () => {
    clearPending();
    setStep('email');
    setCode(Array(CODE_LENGTH).fill(''));
    setError('');
    setCooldownUntil(0);
  };

  const codeString = code.join('');
  const cooldownRemaining = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));

  return (
    <div>
      <Wordmark>
        <span>thea</span>
      </Wordmark>

      {step === 'done' ? (
        <div data-testid="otp-success">
          <Heading id="thea-auth-heading">You&rsquo;re signed in ✨</Heading>
          <Subhead>Your boards are here whenever you come back.</Subhead>
        </div>
      ) : step === 'email' ? (
        <>
          <Heading id="thea-auth-heading">{INTENT_HEADING[intent]}</Heading>
          <Subhead>Enter your email and we&rsquo;ll send you a 6-digit code — no password.</Subhead>

          <Spacer $h={20} />

          <form onSubmit={handleSendCode} noValidate>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              aria-label="Email"
              aria-invalid={!!error}
              aria-describedby={error ? 'otp-error' : undefined}
              $error={!!error}
              autoFocus
            />
            {error && <ErrorText id="otp-error">{error}</ErrorText>}

            <Spacer $h={16} />

            <PrimaryButton type="submit" disabled={busy}>
              {busy && <Spinner animation="border" size="sm" />}
              {busy ? 'Sending…' : 'Send me a code'}
            </PrimaryButton>
          </form>
        </>
      ) : (
        <>
          <Heading id="thea-auth-heading">Enter your code</Heading>
          <Subhead>
            We sent a 6-digit code to <strong>{email}</strong>.
          </Subhead>

          <Spacer $h={20} />

          <CodeRow role="group" aria-label="6-digit code">
            {code.map((digit, i) => (
              <CodeInput
                key={i}
                ref={(el) => {
                  codeInputsRef.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={digit}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(i, e)}
                onPaste={handleCodePaste}
                aria-label={`Digit ${i + 1}`}
                $error={!!error}
                disabled={busy}
              />
            ))}
          </CodeRow>
          {error && <ErrorText id="otp-error">{error}</ErrorText>}

          <Spacer $h={16} />

          <PrimaryButton
            type="button"
            onClick={() => submitCode(codeString)}
            disabled={busy || codeString.length !== CODE_LENGTH}
          >
            {busy && <Spinner animation="border" size="sm" />}
            {busy ? 'Verifying…' : 'Verify'}
          </PrimaryButton>

          <LinkRow>
            {cooldownRemaining > 0 ? (
              <p>Didn&rsquo;t get it? Resend in {cooldownRemaining}s</p>
            ) : (
              <button type="button" onClick={handleResend} disabled={busy}>
                Didn&rsquo;t get it? Resend
              </button>
            )}
          </LinkRow>

          <LinkRow>
            <button type="button" onClick={handleChangeEmail} disabled={busy}>
              Use a different email
            </button>
          </LinkRow>
        </>
      )}
    </div>
  );
};

const CloseGlyph: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
